import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Truck, Plus, Trash2, Search, Settings, CheckCircle2, AlertTriangle, ArrowLeft, ArrowRight, Send } from 'lucide-react'
import api from '../services/api'
import CidadeSelect from '../components/CidadeSelect'
import NovoClienteModal from '../components/NovoClienteModal'

// Emissão de NFS-e Nacional de LOCAÇÃO DE BENS MÓVEIS (cTribNac 99.04.01) — NT 009 v1.01.
// Locação de ônibus/veículos sem motorista: sem ISSQN (tribISSQN = 4), com o grupo IBSCBS e os bens locados
// (bensMoveis: NCM, descrição e quantidade). IBS/CBS são calculados pela Sefin; os valores aqui são demonstrativos.
// Doação (indDoacao) e split payment (gPgtoVinc) ficaram de fora: não existem no esquema publicado.

interface Cliente { cli_cod: string; cli_mat: string; cli_nom: string; cli_fan: string; bloqueado?: boolean }
interface Empresa { empcod: number; empnom: string; empfan: string }
interface Bem { ncm: string; descricao: string; quantidade: string; ident: string; valorUnit: string }
interface Parcela { numero: string; vencimento: string; valor: string }
interface Veiculo { veicod: string; placa: string; tipo: string; marca: string; modelo: string; ano: string; chassi: string; ncm: string }
interface Resultado { ok: boolean; chaveAcesso?: string; xMotivo?: string; erro?: string; numero?: number; avisoBanco?: string }

const NBS = [
  { cod: '110111100', des: '1.1101.11.00 — Locação de veículos rodoviários automotores para transporte de até oito passageiros, sem operador' },
  { cod: '110111200', des: '1.1101.12.00 — Locação de veículos rodoviários automotores para transporte de cargas, sem operador' },
  { cod: '110111400', des: '1.1101.14.00 — Locação de outros equipamentos de transporte terrestre, inclusive veículos de uso misto, sem operador' },
]
const IND_OP = [
  { cod: '010101', des: '010101 — Estabelecimento do prestador (retirada/disponibilização na empresa)' },
  { cod: '010102', des: '010102 — Local da prestação' },
  { cod: '010103', des: '010103 — Endereço do destinatário' },
  { cod: '010106', des: '010106 — Endereço do tomador/adquirente' },
]
// Alíquotas de teste de 2026 (LC 214): só demonstrativo — quem calcula é a Sefin
const ALIQ_2026 = { cbs: 0.9, ibsUf: 0.1, ibsMun: 0 }
const ETAPAS = ['Tomador', 'Operação e bens', 'Tributário', 'Fatura e complementares', 'Revisão']

const num = (v: string) => { const s = String(v ?? '').trim(); return s.includes(',') ? Number(s.replace(/\./g, '').replace(',', '.')) || 0 : Number(s) || 0 }
const moeda = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const hoje = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` }
const somaDias = (iso: string, dias: number) => { const d = new Date(iso + 'T12:00:00'); d.setDate(d.getDate() + dias); return d.toISOString().slice(0, 10) }
const br = (iso: string) => (iso ? iso.split('-').reverse().join('/') : '')
const fmtDoc = (d: string) => { const x = d.replace(/\D/g, ''); return x.length === 14 ? x.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5') : x.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') }
const fmtNcm = (n: string) => n.replace(/\D/g, '').replace(/^(\d{4})(\d{2})(\d{2})$/, '$1.$2.$3')
const bemVazio = (): Bem => ({ ncm: '', descricao: '', quantidade: '1', ident: '', valorUnit: '' })

export default function EmitirNFSeLocacao() {
  const [sp] = useSearchParams()
  const copyId = sp.get('copyId') ? Number(sp.get('copyId')) : null
  const editId = sp.get('editId') ? Number(sp.get('editId')) : null
  const empParam = sp.get('empcod') ? Number(sp.get('empcod')) : null

  const [etapa, setEtapa] = useState(0)
  const [erro, setErro] = useState('')
  const [empresas, setEmpresas] = useState<Empresa[]>([])
  const [empresa, setEmpresa] = useState<Empresa | null>(null)
  const [serie, setSerie] = useState('7000')
  const [nDPS, setNDPS] = useState('')
  const [competencia, setCompetencia] = useState(hoje())

  // Tomador
  const [busca, setBusca] = useState('')
  const [clientes, setClientes] = useState<Cliente[]>([])
  const [cliente, setCliente] = useState<Cliente | null>(null)
  const [modalNovo, setModalNovo] = useState(false)
  const [endLogradouro, setEndLogradouro] = useState('')
  const [endNumero, setEndNumero] = useState('SN')
  const [endBairro, setEndBairro] = useState('')
  const [endCep, setEndCep] = useState('')
  const [endCidade, setEndCidade] = useState('')
  const [endCMun, setEndCMun] = useState('')
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Operação e bens
  const [cNBS, setCNBS] = useState(NBS[2].cod)
  const [cIndOp, setCIndOp] = useState('010101')
  const [locCod, setLocCod] = useState(''); const [locDes, setLocDes] = useState(''); const [locIbge, setLocIbge] = useState(''); const [locUf, setLocUf] = useState('')
  const [xDescServ, setXDescServ] = useState('')
  const [bens, setBens] = useState<Bem[]>([])
  const [vServ, setVServ] = useState('')
  const [vServManual, setVServManual] = useState(false)
  const [vDescIncond, setVDescIncond] = useState('')
  const [vDescCond, setVDescCond] = useState('')
  const [buscaVeiculo, setBuscaVeiculo] = useState<string | null>(null)
  const [veiculos, setVeiculos] = useState<Veiculo[]>([])
  const [ncmTipos, setNcmTipos] = useState<{ tipo: string; qtd: number; ncm: string }[] | null>(null)

  // Tributário / referências
  const [refs, setRefs] = useState<string[]>([])

  // Fatura e complementares
  const [parcelas, setParcelas] = useState<Parcela[]>([])
  const [contaCod, setContaCod] = useState(''); const [contaNome, setContaNome] = useState('')
  const [contas, setContas] = useState<{ dep_cod: string; dep_des: string }[]>([])
  const [xPed, setXPed] = useState('')
  const [xItemPed, setXItemPed] = useState('')
  const [xInfComp, setXInfComp] = useState('')

  const [enviando, setEnviando] = useState(false)
  const [resultado, setResultado] = useState<Resultado | null>(null)
  const [ambiente, setAmbiente] = useState<'hom' | 'prod' | ''>('')
  useEffect(() => { api.get('/nfse-nacional/locacao/ambiente').then(r => setAmbiente(r.data.ambiente)).catch(() => {}) }, [])

  useEffect(() => {
    api.get('/erp/empresas').then(r => {
      setEmpresas(r.data)
      setEmpresa(r.data.find((e: Empresa) => e.empcod === empParam) ?? r.data[0] ?? null)
    }).catch(() => {})
  }, [empParam])

  useEffect(() => {
    if (editId || !empresa) return
    api.get(`/nfse-nacional/proximonumero/${empresa.empcod}`)
      .then(r => { setSerie(String(r.data.serie ?? '7000')); setNDPS(String(r.data.numero ?? '')) }).catch(() => {})
  }, [empresa, editId])

  useEffect(() => {
    if (xDescServ) return
    const [a, m] = competencia.split('-')
    setXDescServ(`Locação de bens móveis (veículos sem motorista) referente à competência ${m}/${a}.`)
  }, [competencia]) // eslint-disable-line react-hooks/exhaustive-deps

  // Cópia ou edição de nota pendente/rejeitada: dados vêm do DPS gravado
  useEffect(() => {
    const id = editId ?? copyId
    if (!id) return
    api.get(`/nfse-nacional/${id}/copia?empcod=${empParam ?? 1}`).then(r => {
      const d = r.data
      if (d.tomador) {
        setCliente(d.tomador)
        api.get(`/erp/clientes/${d.tomador.cli_cod}/endereco`).then(r2 => { setEndCMun(r2.data?.ibge || ''); setEndCidade(r2.data?.cidade ? `${r2.data.cidade}/${r2.data.uf}` : '') }).catch(() => {})
      }
      setEndLogradouro(d.endLogradouro || ''); setEndNumero(d.endNumero || 'SN'); setEndBairro(d.endBairro || ''); setEndCep(d.endCep || '')
      if (d.cNBS) setCNBS(d.cNBS)
      if (d.cLocPrest) setLocIbge(d.cLocPrest)
      if (d.xDescServ) setXDescServ(d.xDescServ)
      if (d.vServ) { setVServ(String(d.vServ)); setVServManual(true) }
      if (d.xInfComp) setXInfComp(d.xInfComp)
      const l = d.locacao
      if (l) {
        setCIndOp(l.cIndOp || '010101')
        setBens((l.bens ?? []).map((b: any) => ({ ncm: b.ncm, descricao: b.descricao, quantidade: String(b.quantidade), ident: '', valorUnit: '' })))
        setVDescIncond(l.vDescIncond ? String(l.vDescIncond) : ''); setVDescCond(l.vDescCond ? String(l.vDescCond) : '')
        setRefs(l.refNFSe ?? []); setXPed(l.xPed ?? ''); setXItemPed((l.xItemPed ?? []).join(', '))
      }
      if (editId) { if (d.serie) setSerie(String(d.serie)); if (d.numero) setNDPS(String(d.numero)); setCompetencia(d.dCompet || hoje()) }
      setEtapa(1)
    }).catch(() => setErro('Não foi possível carregar a nota.'))
  }, [editId, copyId, empParam])

  // ── Valores ──
  const totalBens = useMemo(() => bens.reduce((s, b) => s + num(b.quantidade) * num(b.valorUnit), 0), [bens])
  useEffect(() => { if (!vServManual && totalBens > 0) setVServ(totalBens.toFixed(2)) }, [totalBens, vServManual])
  const vServN = num(vServ), descI = num(vDescIncond), descC = num(vDescCond)
  const base = Math.max(0, vServN - descI)
  const liquido = Math.max(0, vServN - descI - descC)
  const cbs = base * ALIQ_2026.cbs / 100, ibsUf = base * ALIQ_2026.ibsUf / 100, ibsMun = base * ALIQ_2026.ibsMun / 100
  const anoEmissao = Number(competencia.slice(0, 4))
  const esperadoFatura = anoEmissao >= 2027 ? liquido + cbs + ibsUf + ibsMun : liquido // a partir de 2027 IBS/CBS por fora
  const totalParcelas = parcelas.reduce((s, p) => s + num(p.valor), 0)
  const difFatura = esperadoFatura - totalParcelas

  // ── Tomador ──
  function buscarCliente(v: string) {
    setBusca(v)
    if (timer.current) clearTimeout(timer.current)
    if (v.trim().length < 2) { setClientes([]); return }
    timer.current = setTimeout(async () => {
      try { const r = await api.get('/erp/clientes/lista', { params: { busca: v, limit: 10 } }); setClientes(r.data.clientes ?? r.data ?? []) } catch { setClientes([]) }
    }, 350)
  }
  function escolherCliente(c: Cliente) {
    if (c.bloqueado) { setErro('Cliente bloqueado — não é possível emitir documentos para este cliente.'); return }
    setErro(''); setCliente(c); setClientes([]); setBusca('')
    api.get(`/erp/clientes/${c.cli_cod}/endereco`).then(r => {
      const e = r.data || {}
      setEndLogradouro(e.logradouro || ''); setEndNumero(e.numero || 'SN'); setEndBairro(e.bairro || ''); setEndCep(String(e.cep || '').replace(/\D/g, ''))
      setEndCMun(e.ibge || ''); setEndCidade(e.cidade ? `${e.cidade}/${e.uf}` : '')
    }).catch(() => {})
    setEtapa(1)
  }

  // ── Bens ──
  const setBem = (i: number, campo: keyof Bem, v: string) => setBens(bs => bs.map((b, j) => (j === i ? { ...b, [campo]: v } : b)))
  async function procurarVeiculos(v: string) {
    setBuscaVeiculo(v)
    try { const r = await api.get('/nfse-nacional/locacao/veiculos', { params: { busca: v } }); setVeiculos(r.data) } catch { setVeiculos([]) }
  }
  function adicionarVeiculo(v: Veiculo) {
    const nome = { ONI: 'Ônibus', MIC: 'Micro-ônibus', VOL: 'Micro-ônibus', PAS: 'Automóvel', TAX: 'Automóvel' }[v.tipo] ?? 'Veículo'
    const desc = [`${nome} ${v.marca} ${v.modelo}`.replace(/\s+/g, ' ').trim(), v.ano && `ano ${v.ano}`, v.placa && `placa ${v.placa}`, 'sem operador'].filter(Boolean).join(', ')
    setBens(bs => [...bs, { ncm: v.ncm, descricao: desc.slice(0, 150), quantidade: '1', ident: `Veículo ${v.veicod}${v.chassi ? ' · chassi ' + v.chassi : ''}`, valorUnit: '' }])
    if (!v.ncm) setErro(`O tipo de veículo "${v.tipo}" está sem NCM configurado — informe o NCM do bem ou configure em "NCM por tipo".`)
  }
  async function abrirNcmTipos() {
    try { const r = await api.get('/nfse-nacional/locacao/ncm-tipos'); setNcmTipos(r.data) } catch { setErro('Não foi possível carregar os tipos de veículo.') }
  }
  async function salvarNcmTipos() {
    try { await api.put('/nfse-nacional/locacao/ncm-tipos', { tipos: ncmTipos }); setNcmTipos(null) }
    catch (e: any) { setErro(e?.response?.data?.error || 'Erro ao salvar.') }
  }

  // ── Fatura ──
  function gerarParcelas(qtd: number) {
    const n = Math.min(50, Math.max(1, qtd))
    const valor = Math.floor((esperadoFatura / n) * 100) / 100
    setParcelas(Array.from({ length: n }, (_, i) => ({
      numero: String(i + 1), vencimento: somaDias(hoje(), 30 * (i + 1)),
      valor: (i === n - 1 ? esperadoFatura - valor * (n - 1) : valor).toFixed(2),
    })))
  }
  async function buscarContas(v: string) {
    setContaNome(v); setContaCod('')
    if (v.trim().length < 2) { setContas([]); return }
    try { const r = await api.get('/erp/contas', { params: { busca: v } }); setContas(r.data) } catch { setContas([]) }
  }
  function textoFaturas() {
    if (!parcelas.length) return
    const linhas = parcelas.map(p => `${p.numero}) venc. ${br(p.vencimento)} ${moeda(num(p.valor))}`).join('; ')
    setXInfComp(x => `${x.replace(/\s*Faturas: .*$/s, '').trim()}${x.trim() ? ' ' : ''}Faturas: ${linhas}`.slice(0, 2000))
  }

  // ── Validação por etapa ──
  function validar(ate: number): string {
    if (!cliente) return 'Selecione o tomador.'
    if (ate >= 1) {
      if (endCep.replace(/\D/g, '').length !== 8 || !endLogradouro.trim() || !endBairro.trim()) return 'Endereço do tomador incompleto (CEP, logradouro e bairro).'
      if (!xDescServ.trim()) return 'Informe a descrição da operação.'
      if (!bens.length) return 'Informe ao menos um bem móvel locado.'
      for (const [i, b] of bens.entries()) {
        if (b.ncm.replace(/\D/g, '').length !== 8) return `Bem ${i + 1}: NCM deve ter 8 dígitos.`
        if (!b.descricao.trim()) return `Bem ${i + 1}: informe a descrição.`
        const q = num(b.quantidade)
        if (!Number.isInteger(q) || q < 1 || q > 999) return `Bem ${i + 1}: quantidade deve ser de 1 a 999.`
      }
      if (vServN <= 0) return 'Informe o valor do serviço.'
      if (descI + descC >= vServN) return 'Os descontos não podem ser iguais ou maiores que o valor do serviço.'
    }
    if (ate >= 2 && refs.some(c => c.replace(/\D/g, '').length !== 50)) return 'Chave de NFS-e referenciada deve ter 50 dígitos.'
    if (ate >= 3 && parcelas.length) {
      if (!contaCod) return 'Informe a conta financeira da fatura.'
      if (Math.abs(difFatura) >= 0.01) return `A soma das parcelas (${moeda(totalParcelas)}) precisa fechar com o valor esperado (${moeda(esperadoFatura)}).`
      if (parcelas.some(p => !p.vencimento || num(p.valor) <= 0)) return 'Toda parcela precisa de vencimento e valor.'
    }
    return ''
  }
  function irPara(e: number) {
    if (e > etapa) { const m = validar(e - 1); if (m) { setErro(m); return } }
    setErro(''); setEtapa(e)
  }

  async function emitir() {
    const m = validar(4)
    if (m) { setErro(m); return }
    setEnviando(true); setResultado(null); setErro('')
    try {
      const r = await api.post('/nfse-nacional/emitir', {
        empcod: empresa?.empcod ?? 1, serie: serie || undefined, nDPS: nDPS ? Number(nDPS) : undefined,
        docToma: cliente!.cli_mat.replace(/\D/g, ''), nomeToma: cliente!.cli_nom,
        endLogradouro, endNumero: endNumero || 'SN', endBairro, endCep: endCep.replace(/\D/g, ''), endCMun: endCMun || locIbge || undefined,
        dCompet: competencia, cTribNac: '990401', cNBS, cLocPrest: locIbge || undefined,
        xDescServ: xDescServ.trim(), vServ: vServN, tpRetISSQN: '1', xInfComp: xInfComp.trim() || undefined,
        locacao: {
          cIndOp, CST: '000', cClassTrib: '000001',
          bens: bens.map(b => ({ ncm: b.ncm.replace(/\D/g, ''), descricao: b.descricao.trim(), quantidade: num(b.quantidade) })),
          vDescIncond: descI || undefined, vDescCond: descC || undefined,
          refNFSe: refs.map(c => c.replace(/\D/g, '')).filter(Boolean), tpOper: refs.length ? '3' : undefined,
          xPed: xPed.trim() || undefined, xItemPed: xItemPed.split(',').map(s => s.trim()).filter(Boolean),
        },
      })
      setResultado(r.data)
      if (r.data.ok && r.data.numero && parcelas.length && contaCod) {
        await api.post(`/nfse-nacional/${r.data.numero}/financeiro?empcod=${empresa?.empcod ?? 1}`, { parcelas, contaCod, retencaoTotal: 0 })
          .catch(ef => setErro(`NFS-e autorizada, mas o financeiro não foi gravado: ${ef?.response?.data?.error || ef?.message}. Lance pela lista (botão R$).`))
      }
    } catch (e: any) {
      setResultado({ ok: false, erro: e?.response?.data?.error ?? e.message })
    } finally {
      setEnviando(false)
    }
  }

  // ── Estilos ──
  const card: React.CSSProperties = { border: '1px solid #c7d6ea', borderRadius: 12, background: '#fff', padding: '0.9rem 1rem 1rem', boxShadow: '0 6px 18px -10px rgba(15,42,82,0.35)' }
  const titulo: React.CSSProperties = { margin: '-0.9rem -1rem 0.9rem', padding: '0.55rem 1rem', background: 'linear-gradient(135deg, #e8f1fb, #d6e6f7)', borderBottom: '1px solid #c7d6ea', borderLeft: '4px solid #1a6ea8', borderRadius: '12px 12px 0 0', fontSize: '0.74rem', fontWeight: 800, color: '#1b3f7b', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }
  const grade = (c: string): React.CSSProperties => ({ display: 'grid', gridTemplateColumns: c, gap: '0.75rem 1rem', alignItems: 'start' })
  const cheio: React.CSSProperties = { width: '100%', boxSizing: 'border-box' }
  const dica: React.CSSProperties = { fontSize: '0.7rem', color: '#64748b', marginTop: 3 }
  const linhaResumo = (rot: string, val: React.ReactNode, forte = false) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '4px 0', borderBottom: '1px dashed #e2e8f0', fontSize: '0.82rem' }}>
      <span style={{ color: '#64748b' }}>{rot}</span><span style={{ fontWeight: forte ? 800 : 600, color: '#1f2937', textAlign: 'right' }}>{val}</span>
    </div>
  )

  if (resultado?.ok) {
    return (
      <div style={{ maxWidth: 760 }}>
        <div style={{ ...card, borderColor: '#86efac' }}>
          <h3 style={{ marginTop: 0, color: '#15803d', display: 'flex', alignItems: 'center', gap: 8 }}><CheckCircle2 /> NFS-e de locação autorizada</h3>
          {linhaResumo('Número', resultado.numero ?? '—', true)}
          {linhaResumo('Chave de acesso', <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{resultado.chaveAcesso}</span>)}
          {resultado.avisoBanco && <p style={{ color: '#b45309', fontSize: '0.82rem' }}>{resultado.avisoBanco}</p>}
          {erro && <p style={{ color: '#b91c1c', fontSize: '0.82rem' }}>{erro}</p>}
          <div style={{ display: 'flex', gap: 8, marginTop: '1rem' }}>
            <button className="btn btn-primary" onClick={() => window.location.assign('/nfse/locacao')}>Nova locação</button>
            <button className="btn btn-outline" onClick={() => window.location.assign('/nfse/lista')}>Ir para a lista</button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 290px', gap: '1rem', alignItems: 'start', maxWidth: 1280 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem', minWidth: 0 }}>
        <div className="modal-box" style={{ width: '100%', maxWidth: 'none', maxHeight: 'none' }}>
          <div className="modal-header" style={{ background: 'linear-gradient(135deg, #1a6ea8, #14547f)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 9, background: 'rgba(255,255,255,0.15)' }}><Truck size={18} color="#fff" /></span>
              <div>
                <h2>NFS-e Locação de Bens Móveis {editId ? `— editando nº ${editId}` : ''}</h2>
                <div style={{ fontSize: '0.7rem', color: '#dbeafe', marginTop: 2 }}>Código de tributação nacional 99.04.01 · NT 009 v1.01 · sem ISSQN</div>
                {ambiente && (
                  <span style={{ display: 'inline-block', marginTop: 4, fontSize: '0.68rem', fontWeight: 800, borderRadius: 999, padding: '1px 9px',
                    background: ambiente === 'hom' ? '#fde68a' : '#bbf7d0', color: ambiente === 'hom' ? '#78350f' : '#14532d' }}>
                    {ambiente === 'hom' ? 'PRODUÇÃO RESTRITA (teste — sem valor fiscal)' : 'PRODUÇÃO'}
                  </span>
                )}
              </div>
            </div>
            <select value={empresa?.empcod ?? ''} disabled={!!editId} onChange={e => setEmpresa(empresas.find(x => x.empcod === Number(e.target.value)) ?? null)}
              style={{ borderRadius: 6, padding: '4px 8px', border: 'none', fontSize: '0.8rem' }}>
              {empresas.map(e => <option key={e.empcod} value={e.empcod}>{e.empcod} - {e.empfan || e.empnom}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 4, padding: '0.6rem 1rem', background: '#f1f5fb', borderBottom: '1px solid #dbe4f0', flexWrap: 'wrap' }}>
            {ETAPAS.map((t, i) => (
              <button key={t} type="button" onClick={() => irPara(i)}
                style={{ border: 'none', borderRadius: 999, padding: '5px 12px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer',
                  background: i === etapa ? '#1b3f7b' : i < etapa ? '#dbeafe' : '#fff', color: i === etapa ? '#fff' : '#1b3f7b' }}>
                {i + 1}. {t}
              </button>
            ))}
          </div>
        </div>

        {/* 1. Tomador */}
        {etapa === 0 && (
          <div style={card}>
            <div style={titulo}>1. Tomador</div>
            <div style={grade('minmax(0, 1fr) auto')}>
              <div className="field" style={{ margin: 0, position: 'relative' }}>
                <label>Buscar tomador (nome, fantasia ou CNPJ/CPF)</label>
                <input value={busca} onChange={e => buscarCliente(e.target.value)} placeholder="Digite ao menos 2 caracteres" style={cheio} autoFocus />
                {clientes.length > 0 && (
                  <div style={{ position: 'absolute', zIndex: 30, top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #d1d5db', borderRadius: 6, boxShadow: '0 8px 20px rgba(0,0,0,.12)', maxHeight: 280, overflowY: 'auto' }}>
                    {clientes.map(c => (
                      <div key={c.cli_cod} onMouseDown={() => escolherCliente(c)} style={{ padding: '7px 10px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }}>
                        <div style={{ fontWeight: 700, fontSize: '0.84rem' }}>{c.cli_nom} {c.bloqueado && <span style={{ color: '#b91c1c' }}>(bloqueado)</span>}</div>
                        <div style={{ fontSize: '0.74rem', color: '#64748b' }}>{c.cli_fan} · {fmtDoc(c.cli_mat)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button className="btn btn-outline" style={{ marginTop: 22 }} onClick={() => setModalNovo(true)}>+ Novo cliente</button>
            </div>
            {cliente && <div style={{ marginTop: '0.8rem', fontSize: '0.84rem' }}>Selecionado: <b>{cliente.cli_nom}</b> · {fmtDoc(cliente.cli_mat)} <button className="btn btn-sm btn-primary" style={{ marginLeft: 8 }} onClick={() => irPara(1)}>Continuar</button></div>}
          </div>
        )}

        {/* 2. Operação e bens */}
        {etapa === 1 && (<>
          <div style={card}>
            <div style={titulo}>Dados da operação</div>
            <div style={grade('130px 110px 110px minmax(0, 1fr)')}>
              <div className="field" style={{ margin: 0 }}><label>Competência *</label><input type="date" value={competencia} disabled={!!editId} onChange={e => setCompetencia(e.target.value)} style={cheio} /></div>
              <div className="field" style={{ margin: 0 }}><label>Série</label><input value={serie} disabled style={cheio} /></div>
              <div className="field" style={{ margin: 0 }}><label>Nº da DPS</label><input value={nDPS} disabled style={cheio} /></div>
              <div className="field" style={{ margin: 0 }}><label>Tomador</label><input value={cliente ? `${cliente.cli_nom} · ${fmtDoc(cliente.cli_mat)}` : ''} disabled style={cheio} /></div>
            </div>
            <div style={{ ...grade('120px minmax(0, 2fr) 90px minmax(0, 1fr) minmax(0, 1fr)'), marginTop: '0.75rem' }}>
              <div className="field" style={{ margin: 0 }}><label>CEP *</label><input value={endCep} maxLength={8} onChange={e => setEndCep(e.target.value.replace(/\D/g, ''))} style={cheio} /></div>
              <div className="field" style={{ margin: 0 }}><label>Logradouro *</label><input value={endLogradouro} maxLength={125} onChange={e => setEndLogradouro(e.target.value)} style={cheio} /></div>
              <div className="field" style={{ margin: 0 }}><label>Número</label><input value={endNumero} maxLength={10} onChange={e => setEndNumero(e.target.value)} style={cheio} /></div>
              <div className="field" style={{ margin: 0 }}><label>Bairro *</label><input value={endBairro} maxLength={72} onChange={e => setEndBairro(e.target.value)} style={cheio} /></div>
              <div className="field" style={{ margin: 0 }}><label>Cidade</label><input value={endCidade} disabled style={cheio} /></div>
            </div>
            <div style={{ ...grade('170px minmax(0, 1fr)'), marginTop: '0.75rem' }}>
              <div className="field" style={{ margin: 0 }}><label>Cód. Tributação Nacional</label><input value="99.04.01 — Locação de bens móveis" disabled style={cheio} /></div>
              <div className="field" style={{ margin: 0 }}>
                <label>Código NBS *</label>
                <select value={cNBS} onChange={e => setCNBS(e.target.value)} style={cheio}>{NBS.map(n => <option key={n.cod} value={n.cod}>{n.des}</option>)}</select>
              </div>
            </div>
            <div style={{ ...grade('minmax(0, 1fr) minmax(0, 1fr)'), marginTop: '0.75rem' }}>
              <div className="field" style={{ margin: 0 }}>
                <label>Indicador da operação (cIndOp) *</label>
                <select value={cIndOp} onChange={e => setCIndOp(e.target.value)} style={cheio}>{IND_OP.map(o => <option key={o.cod} value={o.cod}>{o.des}</option>)}</select>
              </div>
              <div className="field" style={{ margin: 0 }}>
                <label>Local da prestação</label>
                <CidadeSelect codValue={locCod} desValue={locDes} ibgeValue={locIbge} ufValue={locUf}
                  onChange={(cod, des, ibge, uf) => { setLocCod(cod); setLocDes(des); setLocIbge(ibge); setLocUf(uf) }} />
                <div style={dica}>Em branco = município da empresa.</div>
              </div>
            </div>
            <div className="field" style={{ margin: '0.75rem 0 0' }}>
              <label>Descrição da operação *</label>
              <textarea value={xDescServ} maxLength={2000} rows={2} onChange={e => setXDescServ(e.target.value)} style={{ ...cheio, resize: 'vertical' }} />
            </div>
          </div>

          <div style={card}>
            <div style={titulo}>
              <span>Bens móveis locados ({bens.length})</span>
              <span style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn-sm" style={{ background: '#fff', color: '#1b3f7b', border: '1px solid #c7d6ea', display: 'inline-flex', alignItems: 'center', gap: 4 }} onClick={abrirNcmTipos}><Settings size={13} /> NCM por tipo</button>
                <button className="btn btn-sm btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }} onClick={() => procurarVeiculos('')}><Search size={13} /> Buscar veículo</button>
                <button className="btn btn-sm btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }} onClick={() => setBens(b => [...b, bemVazio()])}><Plus size={13} /> Adicionar bem</button>
              </span>
            </div>
            {bens.length === 0 && <p style={{ color: '#94a3b8', fontSize: '0.84rem', margin: 0 }}>Nenhum bem informado. Busque um veículo da frota ou adicione manualmente (NCM, descrição e quantidade vão no XML).</p>}
            {bens.map((b, i) => (
              <div key={i} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.6rem 0.7rem', marginBottom: 8, background: i % 2 ? '#f8fbff' : '#fff' }}>
                <div style={grade('110px minmax(0, 1fr) 70px 120px 34px')}>
                  <div className="field" style={{ margin: 0 }}><label>NCM *</label><input value={fmtNcm(b.ncm)} maxLength={10} onChange={e => setBem(i, 'ncm', e.target.value.replace(/\D/g, '').slice(0, 8))} style={cheio} /></div>
                  <div className="field" style={{ margin: 0 }}><label>Descrição do bem *</label><input value={b.descricao} maxLength={150} onChange={e => setBem(i, 'descricao', e.target.value)} style={cheio} /></div>
                  <div className="field" style={{ margin: 0 }}><label>Qtd *</label><input type="number" min={1} max={999} value={b.quantidade} onChange={e => setBem(i, 'quantidade', e.target.value)} style={cheio} /></div>
                  <div className="field" style={{ margin: 0 }}><label>Valor unit.</label><input type="number" step="0.01" min={0} value={b.valorUnit} onChange={e => { setBem(i, 'valorUnit', e.target.value); setVServManual(false) }} style={cheio} /></div>
                  <button className="btn btn-sm" title="Excluir bem" style={{ marginTop: 22, background: '#fff', color: '#b91c1c', border: '1px solid #fecaca', padding: '0.35rem' }} onClick={() => setBens(bs => bs.filter((_, j) => j !== i))}><Trash2 size={14} /></button>
                </div>
                {b.ident && <div style={dica}>{b.ident} (controle interno, não vai no XML)</div>}
              </div>
            ))}
          </div>

          <div style={card}>
            <div style={titulo}>Valores</div>
            <div style={grade('repeat(3, minmax(0, 1fr))')}>
              <div className="field" style={{ margin: 0 }}>
                <label>Valor do serviço *</label>
                <input type="number" step="0.01" min={0} value={vServ} onChange={e => { setVServ(e.target.value); setVServManual(true) }} style={cheio} />
                <div style={dica}>{vServManual && totalBens > 0 ? <a href="#" onClick={ev => { ev.preventDefault(); setVServManual(false) }}>Compor pela soma dos bens ({moeda(totalBens)})</a> : 'Soma de quantidade × valor unitário dos bens.'}</div>
              </div>
              <div className="field" style={{ margin: 0 }}><label>Desconto incondicional</label><input type="number" step="0.01" min={0} value={vDescIncond} onChange={e => setVDescIncond(e.target.value)} style={cheio} /><div style={dica}>Reduz a base do IBS/CBS.</div></div>
              <div className="field" style={{ margin: 0 }}><label>Desconto condicional</label><input type="number" step="0.01" min={0} value={vDescCond} onChange={e => setVDescCond(e.target.value)} style={cheio} /><div style={dica}>Reduz só o valor a receber.</div></div>
            </div>
          </div>
        </>)}

        {/* 3. Tributário */}
        {etapa === 2 && (<>
          <div style={card}>
            <div style={titulo}>Regra IBS/CBS</div>
            <div style={grade('90px 110px minmax(0, 1fr)')}>
              <div className="field" style={{ margin: 0 }}><label>CST</label><input value="000" disabled style={cheio} /></div>
              <div className="field" style={{ margin: 0 }}><label>cClassTrib</label><input value="000001" disabled style={cheio} /></div>
              <div className="field" style={{ margin: 0 }}><label>Classificação</label><input value="Situações tributadas integralmente pelo IBS e CBS" disabled style={cheio} /></div>
            </div>
            <div style={{ ...grade('repeat(4, minmax(0, 1fr))'), marginTop: '0.75rem' }}>
              <div className="field" style={{ margin: 0 }}><label>Base IBS/CBS</label><input value={moeda(base)} disabled style={cheio} /></div>
              <div className="field" style={{ margin: 0 }}><label>CBS ({ALIQ_2026.cbs.toFixed(2)}%)</label><input value={moeda(cbs)} disabled style={cheio} /></div>
              <div className="field" style={{ margin: 0 }}><label>IBS UF ({ALIQ_2026.ibsUf.toFixed(2)}%)</label><input value={moeda(ibsUf)} disabled style={cheio} /></div>
              <div className="field" style={{ margin: 0 }}><label>IBS Mun. ({ALIQ_2026.ibsMun.toFixed(2)}%)</label><input value={moeda(ibsMun)} disabled style={cheio} /></div>
            </div>
            <p style={{ ...dica, fontSize: '0.75rem' }}>Valores demonstrativos com as alíquotas de teste de 2026 — o cálculo oficial é feito pela Sefin na autorização. ISSQN: não incidência (locação de bens móveis).</p>
          </div>
          <div style={card}>
            <div style={titulo}>
              <span>Pagamento antecipado — NFS-e referenciadas ({refs.length})</span>
              <button className="btn btn-sm btn-primary" disabled={refs.length >= 99} onClick={() => setRefs(r => [...r, ''])} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Plus size={13} /> Adicionar chave</button>
            </div>
            {refs.length === 0 && <p style={{ ...dica, margin: 0 }}>Opcional. Informe a chave (50 dígitos) da NFS-e do adiantamento, do mesmo prestador. Só vincula os documentos: não reduz a base nem a fatura — a baixa do adiantamento é feita no Contas a Receber.</p>}
            {refs.map((c, i) => (
              <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                <input value={c} maxLength={50} placeholder="Chave da NFS-e (50 dígitos)" onChange={e => setRefs(r => r.map((x, j) => (j === i ? e.target.value.replace(/\D/g, '') : x)))}
                  style={{ ...cheio, fontFamily: 'monospace', padding: '0.45rem 0.6rem', border: '1.5px solid #cbd5e1', borderRadius: 6 }} />
                <button className="btn btn-sm" style={{ background: '#fff', color: '#b91c1c', border: '1px solid #fecaca' }} onClick={() => setRefs(r => r.filter((_, j) => j !== i))}><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </>)}

        {/* 4. Fatura e complementares */}
        {etapa === 3 && (<>
          <div style={card}>
            <div style={titulo}>
              <span>Fatura / cobrança ({parcelas.length}/50)</span>
              <span style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn-sm btn-primary" onClick={() => gerarParcelas(Math.max(1, parcelas.length || 1))}>Gerar fatura</button>
                <button className="btn btn-sm btn-primary" disabled={parcelas.length >= 50} onClick={() => setParcelas(p => [...p, { numero: String(p.length + 1), vencimento: somaDias(hoje(), 30 * (p.length + 1)), valor: Math.max(0, difFatura).toFixed(2) }])}><Plus size={13} /> Parcela</button>
              </span>
            </div>
            <div style={grade('minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)')}>
              {linhaResumo('Valor esperado da fatura', moeda(esperadoFatura), true)}
              {linhaResumo('Total das parcelas', moeda(totalParcelas))}
              {linhaResumo('Diferença', <span style={{ color: Math.abs(difFatura) < 0.01 ? '#15803d' : '#b45309' }}>{moeda(difFatura)}</span>)}
            </div>
            <p style={dica}>{anoEmissao >= 2027 ? 'A partir de 2027 o IBS/CBS entra no valor da fatura.' : 'Em 2026 o IBS/CBS é informativo e não entra na fatura: valor esperado = serviço − descontos.'}</p>
            {parcelas.map((p, i) => (
              <div key={i} style={{ ...grade('70px 160px 160px 34px'), marginTop: 6 }}>
                <input value={p.numero} disabled style={{ ...cheio, padding: '0.4rem', border: '1px solid #e2e8f0', borderRadius: 6 }} />
                <input type="date" value={p.vencimento} onChange={e => setParcelas(ps => ps.map((x, j) => (j === i ? { ...x, vencimento: e.target.value } : x)))} style={{ ...cheio, padding: '0.4rem', border: '1.5px solid #cbd5e1', borderRadius: 6 }} />
                <input type="number" step="0.01" value={p.valor} onChange={e => setParcelas(ps => ps.map((x, j) => (j === i ? { ...x, valor: e.target.value } : x)))} style={{ ...cheio, padding: '0.4rem', border: '1.5px solid #cbd5e1', borderRadius: 6, textAlign: 'right' }} />
                <button className="btn btn-sm" style={{ background: '#fff', color: '#b91c1c', border: '1px solid #fecaca', padding: '0.3rem' }} onClick={() => setParcelas(ps => ps.filter((_, j) => j !== i).map((x, j) => ({ ...x, numero: String(j + 1) })))}><Trash2 size={14} /></button>
              </div>
            ))}
            {parcelas.length > 0 && (
              <div className="field" style={{ margin: '0.75rem 0 0', position: 'relative', maxWidth: 520 }}>
                <label>Conta financeira *</label>
                {contaCod && contaNome
                  ? <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #6ee7b7', borderRadius: 6, padding: '6px 8px', background: '#f0fdf4', fontSize: '0.85rem' }}>
                      <span style={{ flex: 1, color: '#065f46', fontWeight: 600 }}>{contaCod} — {contaNome}</span>
                      <button onClick={() => { setContaCod(''); setContaNome('') }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}>×</button>
                    </div>
                  : <>
                      <input value={contaNome} placeholder="Digite para buscar a conta..." onChange={e => buscarContas(e.target.value)} style={cheio} />
                      {contas.length > 0 && (
                        <div style={{ position: 'absolute', zIndex: 30, top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #d1d5db', borderRadius: 6, maxHeight: 200, overflowY: 'auto' }}>
                          {contas.map(c => <div key={c.dep_cod} onMouseDown={() => { setContaCod(c.dep_cod.trim()); setContaNome(c.dep_des); setContas([]) }} style={{ padding: '6px 10px', cursor: 'pointer', fontSize: '0.82rem' }}><b style={{ fontFamily: 'monospace' }}>{c.dep_cod.trim()}</b> {c.dep_des}</div>)}
                        </div>
                      )}
                    </>}
              </div>
            )}
          </div>
          <div style={card}>
            <div style={titulo}><span>Informações complementares</span><button className="btn btn-sm btn-primary" disabled={!parcelas.length} onClick={textoFaturas}>Atualizar com faturas</button></div>
            <div style={grade('minmax(0, 1fr) minmax(0, 1fr)')}>
              <div className="field" style={{ margin: 0 }}><label>Pedido / ordem (xPed)</label><input value={xPed} maxLength={60} onChange={e => setXPed(e.target.value)} style={cheio} /></div>
              <div className="field" style={{ margin: 0 }}><label>Itens do pedido (xItemPed)</label><input value={xItemPed} placeholder="separe por vírgula" onChange={e => setXItemPed(e.target.value)} style={cheio} /></div>
            </div>
            <div className="field" style={{ margin: '0.75rem 0 0' }}>
              <label>Informações complementares (xInfComp)</label>
              <textarea value={xInfComp} maxLength={2000} rows={4} onChange={e => setXInfComp(e.target.value)} style={{ ...cheio, resize: 'vertical' }} />
              <div style={{ ...dica, textAlign: 'right' }}>{xInfComp.length} / 2000</div>
            </div>
          </div>
        </>)}

        {/* 5. Revisão */}
        {etapa === 4 && (
          <div style={card}>
            <div style={titulo}>Revisão</div>
            <div style={grade('minmax(0, 1fr) minmax(0, 1fr)')}>
              <div>
                {linhaResumo('Tomador', cliente?.cli_nom)}
                {linhaResumo('CNPJ/CPF', cliente ? fmtDoc(cliente.cli_mat) : '')}
                {linhaResumo('Endereço', `${endLogradouro}, ${endNumero} · ${endBairro} · CEP ${endCep}`)}
                {linhaResumo('Competência', br(competencia))}
                {linhaResumo('Tributação nacional', '99.04.01')}
                {linhaResumo('NBS', NBS.find(n => n.cod === cNBS)?.des.split(' — ')[0])}
                {linhaResumo('Indicador da operação', cIndOp)}
                {linhaResumo('Bens móveis', `${bens.length} item(ns) · ${bens.reduce((s, b) => s + num(b.quantidade), 0)} unidade(s)`)}
              </div>
              <div>
                {linhaResumo('Valor do serviço', moeda(vServN), true)}
                {linhaResumo('Desconto incondicional', moeda(descI))}
                {linhaResumo('Desconto condicional', moeda(descC))}
                {linhaResumo('CST / cClassTrib', '000 / 000001')}
                {linhaResumo('Base IBS/CBS', moeda(base))}
                {linhaResumo('NFS-e referenciadas', refs.length)}
                {linhaResumo('Faturas', parcelas.length ? `${parcelas.length} · ${moeda(totalParcelas)}` : 'sem fatura')}
                {linhaResumo('Valor esperado da fatura', moeda(esperadoFatura), true)}
              </div>
            </div>
            <div style={{ marginTop: '0.9rem', fontSize: '0.8rem', color: '#334155', background: '#f8fafc', borderRadius: 8, padding: '0.6rem 0.8rem' }}><b>Descrição:</b> {xDescServ}</div>
            {resultado && !resultado.ok && (
              <div style={{ marginTop: '0.8rem', background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 8, padding: '0.6rem 0.8rem', fontSize: '0.82rem' }}>
                <b>Não autorizada:</b> {resultado.erro || resultado.xMotivo}
              </div>
            )}
          </div>
        )}

        {erro && <div style={{ display: 'flex', gap: 8, alignItems: 'center', background: '#fff7ed', color: '#9a3412', border: '1px solid #fed7aa', borderRadius: 8, padding: '0.55rem 0.8rem', fontSize: '0.82rem' }}><AlertTriangle size={16} /> {erro}</div>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
          <button className="btn btn-outline" disabled={etapa === 0} onClick={() => setEtapa(e => Math.max(0, e - 1))} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><ArrowLeft size={15} /> Voltar</button>
          {etapa < 4
            ? <button className="btn btn-primary" onClick={() => irPara(etapa + 1)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>Prosseguir <ArrowRight size={15} /></button>
            : <button className="btn btn-primary" disabled={enviando} onClick={emitir} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Send size={15} /> {enviando ? 'Transmitindo...' : editId ? 'Retransmitir NFS-e' : 'Emitir NFS-e'}</button>}
        </div>
      </div>

      {/* Resumo fiscal lateral */}
      <div style={{ ...card, position: 'sticky', top: 12 }}>
        <div style={titulo}>Resumo fiscal</div>
        {linhaResumo('Valor do serviço', moeda(vServN))}
        {linhaResumo('Desconto incond.', moeda(descI))}
        {linhaResumo('Desconto cond.', moeda(descC))}
        {linhaResumo('Base IBS/CBS', moeda(base), true)}
        {linhaResumo('CBS', moeda(cbs))}
        {linhaResumo('IBS UF', moeda(ibsUf))}
        {linhaResumo('IBS Município', moeda(ibsMun))}
        {linhaResumo('Valor esperado/fatura', moeda(esperadoFatura), true)}
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.78rem' }}>
          {[['Tomador', !!cliente], ['Bens móveis', bens.length > 0 && !validar(1)], ['Valores', vServN > 0], ['Fatura fechada', !parcelas.length || Math.abs(difFatura) < 0.01]].map(([t, ok]) => (
            <span key={String(t)} style={{ color: ok ? '#15803d' : '#b45309', fontWeight: 700 }}>{ok ? '✓' : '!'} {t}</span>
          ))}
        </div>
      </div>

      {modalNovo && <NovoClienteModal onClose={() => setModalNovo(false)} onSalvo={(c: any) => { setModalNovo(false); escolherCliente(c) }} />}

      {buscaVeiculo !== null && (
        <div className="modal-overlay" style={{ zIndex: 1300 }} onClick={e => e.target === e.currentTarget && setBuscaVeiculo(null)}>
          <div className="modal-box" style={{ maxWidth: 720, maxHeight: '86vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header"><h2>Buscar veículo da frota</h2><button className="modal-fechar" onClick={() => setBuscaVeiculo(null)}>✕</button></div>
            <div className="modal-corpo" style={{ overflowY: 'auto' }}>
              <div className="field"><input value={buscaVeiculo} autoFocus placeholder="Código, placa ou modelo" onChange={e => procurarVeiculos(e.target.value)} /></div>
              {veiculos.map(v => (
                <div key={v.veicod} onClick={() => adicionarVeiculo(v)} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '7px 10px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', fontSize: '0.84rem' }}>
                  <span><b>{v.veicod}</b> · {v.placa} · {v.tipo} · {v.marca} {v.modelo} {v.ano}</span>
                  <span style={{ color: v.ncm ? '#1b3f7b' : '#b45309', fontFamily: 'monospace' }}>{v.ncm ? fmtNcm(v.ncm) : 'sem NCM'}</span>
                </div>
              ))}
              <p style={dica}>Clique no veículo para incluir como bem locado. O NCM vem do tipo do veículo ("NCM por tipo").</p>
            </div>
            <div className="modal-footer"><button className="btn btn-secondary" onClick={() => setBuscaVeiculo(null)}>Fechar</button></div>
          </div>
        </div>
      )}

      {ncmTipos && (
        <div className="modal-overlay" style={{ zIndex: 1300 }} onClick={e => e.target === e.currentTarget && setNcmTipos(null)}>
          <div className="modal-box" style={{ maxWidth: 520 }}>
            <div className="modal-header"><h2>NCM por tipo de veículo</h2><button className="modal-fechar" onClick={() => setNcmTipos(null)}>✕</button></div>
            <div className="modal-corpo">
              {ncmTipos.map((t, i) => (
                <div key={t.tipo} style={{ display: 'grid', gridTemplateColumns: '80px 1fr 150px', gap: 8, alignItems: 'center', marginBottom: 6, fontSize: '0.84rem' }}>
                  <b>{t.tipo}</b><span style={{ color: '#64748b' }}>{t.qtd} veículo(s)</span>
                  <input value={fmtNcm(t.ncm)} maxLength={10} placeholder="0000.00.00" onChange={e => setNcmTipos(l => l!.map((x, j) => (j === i ? { ...x, ncm: e.target.value.replace(/\D/g, '').slice(0, 8) } : x)))}
                    style={{ padding: '0.4rem', border: '1.5px solid #cbd5e1', borderRadius: 6, fontFamily: 'monospace' }} />
                </div>
              ))}
              <p style={dica}>8702.10.00 = veículo para 10 ou mais passageiros, motor diesel. Automóveis (8703) dependem da cilindrada — confirme com a contabilidade.</p>
            </div>
            <div className="modal-footer"><button className="btn btn-secondary" onClick={() => setNcmTipos(null)}>Cancelar</button><button className="btn btn-primary" onClick={salvarNcmTipos}>Salvar</button></div>
          </div>
        </div>
      )}
    </div>
  )
}
